import React, { useCallback, useEffect, useState } from "react";
import { getOverview } from "../services/analyticsService";
import { formatCurrency } from "../utils/helpers";
import { SpinnerCustom } from "../components/ui/Spinner";
import {
  canAccessClientAdministration,
  CLIENT_ADMIN_PAGE,
  OVERVIEW_INVOICE_STATUS_KEY,
  OVERVIEW_INVENTORY_FILTERS_KEY,
  OVERVIEW_MASTER_ADDRESS_TAB_KEY,
} from "../config/constants";
import { REPORT20_YTD_BUDGET_PAGE } from "../config/report20Nav";

function formatMoney(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return formatCurrency(Number(n));
}

function formatWholeMoney(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const num = Number(n);
  const formatted = Math.abs(num).toLocaleString("en-US", {
    maximumFractionDigits: 0,
  });
  return num < 0 ? `-$${formatted}` : `$${formatted}`;
}

function formatSignedWholeMoney(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const num = Number(n);
  const formatted = Math.abs(num).toLocaleString("en-US", {
    maximumFractionDigits: 0,
  });
  if (num > 0) return `+$${formatted}`;
  if (num < 0) return `-$${formatted}`;
  return `$${formatted}`;
}

function formatEndDate(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(String(ymd).slice(0, 10))) return "—";
  const [y, m, d] = String(ymd).slice(0, 10).split("-").map(Number);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${months[m - 1]} ${d}, ${y}`;
}

const OverviewPage = ({ tenant, userRole, onNavigate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getOverview();
      if (res?.success && res?.data) {
        setData(res.data);
      } else {
        setData(null);
        setError(res?.message || res?.error || "Failed to load overview");
      }
    } catch (err) {
      setData(null);
      setError(err.message || "Failed to load overview");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const goInvoices = (statusFilter) => {
    try {
      if (statusFilter) {
        sessionStorage.setItem(OVERVIEW_INVOICE_STATUS_KEY, statusFilter);
      } else {
        sessionStorage.removeItem(OVERVIEW_INVOICE_STATUS_KEY);
      }
    } catch {
      /* ignore */
    }
    onNavigate("invoices");
  };

  const goInventory = (filters = {}) => {
    try {
      if (filters && Object.keys(filters).length > 0) {
        sessionStorage.setItem(
          OVERVIEW_INVENTORY_FILTERS_KEY,
          JSON.stringify(filters),
        );
      } else {
        sessionStorage.removeItem(OVERVIEW_INVENTORY_FILTERS_KEY);
      }
    } catch {
      /* ignore */
    }
    onNavigate("inventory");
  };

  const goMasterNeeds = () => {
    try {
      sessionStorage.setItem(OVERVIEW_MASTER_ADDRESS_TAB_KEY, "needs");
    } catch {
      /* ignore */
    }
    onNavigate("master-address");
  };

  const goImportHistory = () => {
    onNavigate(CLIENT_ADMIN_PAGE.IMPORT);
  };

  const goYtdBudget = () => {
    onNavigate(REPORT20_YTD_BUDGET_PAGE);
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen pt-[80px] bg-[var(--ground,#f5f4f1)] flex items-center justify-center">
        <SpinnerCustom />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen pt-[80px] bg-[var(--ground,#f5f4f1)] px-5 py-8">
        <div className="rounded-[10px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      </div>
    );
  }

  const attention = data?.needs_attention || {};
  const budget = data?.budget_pace || {};
  const invoices = data?.invoices_this_month || {};
  const inventory = data?.inventory || {};
  const contracts = data?.contracts_ending_soon || [];
  const stats = data?.stats || {};
  const billing = data?.billing_period || {};

  const canOpenImport = canAccessClientAdministration(userRole);

  const attentionPills = [
    {
      key: "missing_or_needs_review",
      count:
        attention.missing_or_needs_review ||
        attention.flagged_or_past_due ||
        0,
      label: "invoices missing or needing review",
      severity: "crit",
      onClick: () => goInvoices("missing_or_needs_review"),
    },
    {
      key: "missing_gl",
      count: attention.missing_gl || 0,
      label: "invoices missing a GL code",
      severity: "crit",
      onClick: () => goInvoices("missing_gl"),
    },
    {
      key: "addresses_need_master",
      count: attention.addresses_need_master || 0,
      label: "addresses need a master",
      severity: "crit",
      onClick: goMasterNeeds,
    },
    {
      key: "rows_left_out",
      count: canOpenImport ? attention.rows_left_out_last_import || 0 : 0,
      label: "rows left out of the last import",
      severity: "warn",
      onClick: goImportHistory,
    },
    {
      key: "not_sent",
      count: attention.invoices_not_sent_to_ap || 0,
      label: "invoices not yet sent to AP",
      severity: "warn",
      onClick: () => goInvoices("not_sent"),
    },
    {
      key: "expected",
      count:
        attention.accounts_expected_invoice ||
        attention.accounts_awaiting_invoice ||
        0,
      label: "accounts with invoices still expected",
      severity: "muted",
      onClick: () => goInvoices("expected"),
    },
  ].filter((p) => p.count > 0);

  const projectedVariance = budget.projected_year_end_variance;
  const varianceUnder =
    projectedVariance != null && Number(projectedVariance) <= 0;

  const countBadgeClass = {
    crit: "bg-[var(--crit,#c0262b)] text-white",
    warn: "bg-[var(--warn,#b8791e)] text-white",
    muted: "bg-[var(--muted,#6b6a66)] text-white",
  };

  const cardChip = (dotClass, label, onClick) => (
    <span
      role="link"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          onClick();
        }
      }}
      className="inline-flex items-center gap-1.5 text-[12.5px] text-[var(--ink,#1c1c1a)] hover:text-[var(--accent,#a51c1c)] cursor-pointer"
    >
      <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${dotClass}`} />
      {label}
    </span>
  );

  return (
    <div className="min-h-screen pt-[80px] bg-[var(--ground,#f5f4f1)]">
      <div className="w-full px-5 py-2 pb-14 space-y-4">
        {/* Title strip */}
        <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4">
          <div>
            <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[var(--accent,#a51c1c)]">
              {tenant?.name || "Client"}
            </p>
            <h1 className="mt-1 text-[26px] font-semibold leading-tight text-[var(--ink,#1c1c1a)]">
              Overview
            </h1>
            <p className="mt-1 text-[13px] text-[var(--muted,#6b6a66)]">
              {billing.subtitle || "—"}
            </p>
          </div>

          <div className="inline-flex items-stretch rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white shadow-sm overflow-hidden">
            <div className="px-4 py-2.5 border-r border-[var(--line,#e4e2dc)]">
              <p className="text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                Billing period
              </p>
              <p className="mt-0.5 text-[13px] font-semibold text-[var(--ink,#1c1c1a)] tabular-nums">
                {stats.billing_period_label || "—"}
              </p>
            </div>
            <div className="px-4 py-2.5 border-r border-[var(--line,#e4e2dc)]">
              <p className="text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                Inventory MRC
              </p>
              <p className="mt-0.5 text-[13px] font-semibold text-[var(--ink,#1c1c1a)] tabular-nums">
                {formatWholeMoney(stats.inventory_mrc)}
              </p>
            </div>
            <div className="px-4 py-2.5">
              <p className="text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                Last import
              </p>
              <p className="mt-0.5 text-[13px] font-semibold text-[var(--ink,#1c1c1a)]">
                {stats.last_import?.label || "—"}
              </p>
            </div>
          </div>
        </div>

        {/* Needs attention */}
        <div className="rounded-[12px] border border-[var(--line,#e4e2dc)] bg-white px-4 py-3.5 shadow-[0_1px_2px_rgba(28,28,26,0.04)]">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2.5 flex-shrink-0">
              <span
                className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-full border border-[var(--line-strong,#d5d3cc)] text-[11px] font-semibold text-[var(--muted,#6b6a66)]"
                aria-hidden="true"
              >
                !
              </span>
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--muted,#6b6a66)] whitespace-nowrap">
                Needs attention
              </p>
              <span
                className="hidden sm:block h-5 w-px bg-[var(--line,#e4e2dc)]"
                aria-hidden="true"
              />
            </div>
            {attentionPills.length === 0 ? (
              <p className="text-[13px] text-[var(--muted,#6b6a66)]">
                Nothing needs attention
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {attentionPills.map((pill) => (
                  <button
                    key={pill.key}
                    type="button"
                    onClick={pill.onClick}
                    className="inline-flex items-center gap-2 rounded-full border border-[var(--line,#e4e2dc)] bg-white pl-1.5 pr-2.5 py-1 text-[13px] text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken2,#fbfaf8)] transition-colors"
                  >
                    <span
                      className={`inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold tabular-nums ${countBadgeClass[pill.severity]}`}
                    >
                      {pill.count}
                    </span>
                    <span>{pill.label}</span>
                    <span
                      aria-hidden="true"
                      className="text-[var(--faint,#9a9a95)] text-[12px] leading-none"
                    >
                      ›
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Three cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Budget pace */}
          <button
            type="button"
            onClick={goYtdBudget}
            className="text-left rounded-[12px] border border-[var(--line,#e4e2dc)] bg-white px-5 py-5 shadow-[0_1px_2px_rgba(28,28,26,0.04)] hover:shadow-[0_2px_8px_rgba(28,28,26,0.06)] transition-shadow"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[15px] font-semibold text-[var(--ink,#1c1c1a)]">
                  Budget pace
                </p>
                <p className="mt-0.5 text-[12px] text-[var(--muted,#6b6a66)]">
                  YTD Budget Tracking
                </p>
              </div>
              <span className="text-[12px] font-medium text-[var(--accent,#a51c1c)] whitespace-nowrap pt-0.5">
                Open report
              </span>
            </div>
            <p
              className={`mt-4 text-[32px] font-semibold leading-none tracking-tight tabular-nums ${
                projectedVariance == null
                  ? "text-[var(--ink,#1c1c1a)]"
                  : varianceUnder
                    ? "text-[var(--good,#1f6b34)]"
                    : "text-[var(--crit,#c0262b)]"
              }`}
            >
              {formatSignedWholeMoney(projectedVariance)}
            </p>
            <p className="mt-2.5 text-[12.5px] text-[var(--muted,#6b6a66)] leading-snug">
              Projected year end {formatWholeMoney(budget.forecast_eoy_spend)}{" "}
              against a {formatWholeMoney(budget.annual_budget)} budget
              {budget.through_month_label
                ? ` · through ${budget.through_month_label}`
                : ""}
            </p>
            <div className="mt-5 grid grid-cols-3 gap-3">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--muted,#6b6a66)]">
                  YTD actual
                </p>
                <p className="mt-1 text-[13px] font-semibold tabular-nums text-[var(--ink,#1c1c1a)]">
                  {formatWholeMoney(budget.ytd_actual)}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--muted,#6b6a66)]">
                  YTD budget
                </p>
                <p className="mt-1 text-[13px] font-semibold tabular-nums text-[var(--ink,#1c1c1a)]">
                  {formatWholeMoney(budget.ytd_budget)}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--muted,#6b6a66)]">
                  Stabilized rate
                </p>
                <p className="mt-1 text-[13px] font-semibold tabular-nums text-[var(--ink,#1c1c1a)]">
                  {budget.stabilized_monthly_run_rate != null
                    ? `${formatWholeMoney(budget.stabilized_monthly_run_rate)} /mo`
                    : "—"}
                </p>
              </div>
            </div>
          </button>

          {/* Invoices this month */}
          <button
            type="button"
            onClick={() => goInvoices()}
            className="text-left rounded-[12px] border border-[var(--line,#e4e2dc)] bg-white px-5 py-5 shadow-[0_1px_2px_rgba(28,28,26,0.04)] hover:shadow-[0_2px_8px_rgba(28,28,26,0.06)] transition-shadow"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[15px] font-semibold text-[var(--ink,#1c1c1a)]">
                  Invoices this month
                </p>
                <p className="mt-0.5 text-[12px] text-[var(--muted,#6b6a66)]">
                  {invoices.label || "—"}
                </p>
              </div>
              <span className="text-[12px] font-medium text-[var(--accent,#a51c1c)] whitespace-nowrap pt-0.5">
                Open invoices
              </span>
            </div>
            <p className="mt-4 text-[32px] font-semibold leading-none tracking-tight text-[var(--ink,#1c1c1a)] tabular-nums">
              <span className="font-semibold">{invoices.received ?? 0}</span>
              <span className="ml-1.5 text-[18px] font-normal text-[var(--ink,#1c1c1a)]">
                of {invoices.expected ?? 0} received
              </span>
            </p>
            <p className="mt-2.5 text-[12.5px] text-[var(--muted,#6b6a66)] leading-snug">
              {formatWholeMoney(invoices.total_so_far)} so far
              {invoices.like_for_like_change != null
                ? ` · ${formatSignedWholeMoney(invoices.like_for_like_change)} vs the same accounts in ${
                    invoices.prior_month_short?.split(" ")[0] || "prior"
                  }`
                : ""}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2">
              {cardChip(
                "bg-[var(--crit,#c0262b)]",
                `${invoices.missing ?? 0} missing`,
                () => goInvoices("missing"),
              )}
              {cardChip(
                "bg-[var(--crit,#c0262b)]",
                `${invoices.needs_review ?? invoices.flagged ?? 0} need review`,
                () => goInvoices("needs_review"),
              )}
              {cardChip(
                "bg-[var(--muted,#6b6a66)]",
                `${invoices.expected_status ?? invoices.awaiting ?? 0} expected`,
                () => goInvoices("expected"),
              )}
              {cardChip(
                "bg-[var(--good,#1f6b34)]",
                `${invoices.sent_to_ap ?? 0} sent to AP`,
                () => goInvoices("sent"),
              )}
            </div>
          </button>

          {/* Inventory */}
          <button
            type="button"
            onClick={() => goInventory()}
            className="text-left rounded-[12px] border border-[var(--line,#e4e2dc)] bg-white px-5 py-5 shadow-[0_1px_2px_rgba(28,28,26,0.04)] hover:shadow-[0_2px_8px_rgba(28,28,26,0.06)] transition-shadow"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[15px] font-semibold text-[var(--ink,#1c1c1a)]">
                  Inventory
                </p>
                <p className="mt-0.5 text-[12px] text-[var(--muted,#6b6a66)]">
                  Current services
                </p>
              </div>
              <span className="text-[12px] font-medium text-[var(--accent,#a51c1c)] whitespace-nowrap pt-0.5">
                Open inventory
              </span>
            </div>
            <p className="mt-4 text-[32px] font-semibold leading-none tracking-tight text-[var(--ink,#1c1c1a)] tabular-nums">
              <span className="font-semibold">
                {(inventory.service_count ?? 0).toLocaleString()}
              </span>
              <span className="ml-1.5 text-[18px] font-normal text-[var(--ink,#1c1c1a)]">
                services
              </span>
            </p>
            <p className="mt-2.5 text-[12.5px] text-[var(--muted,#6b6a66)] leading-snug">
              {(inventory.site_count ?? 0).toLocaleString()} sites ·{" "}
              {(inventory.provider_count ?? 0).toLocaleString()} providers ·{" "}
              {formatWholeMoney(inventory.total_mrc)} MRC
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2">
              {cardChip(
                "bg-[var(--warn,#b8791e)]",
                `${inventory.contracts_ending_within_180 ?? 0} contract${
                  (inventory.contracts_ending_within_180 ?? 0) === 1 ? "" : "s"
                } ending within 180 days`,
                () => goInventory({ contractEndsWithin: "180" }),
              )}
              {cardChip(
                "bg-[var(--crit,#c0262b)]",
                `${inventory.unmapped_addresses ?? 0} addresses unmapped`,
                goMasterNeeds,
              )}
            </div>
          </button>
        </div>

        {/* Contracts ending soon */}
        <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-4 py-4 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
            <div>
              <h2 className="text-[15px] font-semibold text-[var(--ink,#1c1c1a)]">
                Contracts ending soon
              </h2>
              <p className="mt-0.5 text-[12px] text-[var(--muted,#6b6a66)]">
                Next 180 days from inventory end of term dates. Amber within 90
                days.
              </p>
            </div>
            <button
              type="button"
              onClick={() => goInventory({ contractEndsWithin: "180" })}
              className="text-[12px] font-medium text-[var(--accent,#a51c1c)] whitespace-nowrap self-start"
            >
              See all in inventory
            </button>
          </div>

          {contracts.length === 0 ? (
            <p className="text-[13px] text-[var(--muted,#6b6a66)] py-4">
              No contracts ending within the next 180 days.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left">
                <thead>
                  <tr className="border-b border-[var(--line,#e4e2dc)]">
                    {["Circuit", "Site", "Provider", "MRC", "Ends", "Days"].map(
                      (h) => (
                        <th
                          key={h}
                          className="px-2 py-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]"
                        >
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {contracts.map((row) => {
                    const days = row.days_remaining;
                    const amber = days != null && days <= 90;
                    return (
                      <tr
                        key={row.id || row.circuit_id}
                        onClick={() =>
                          goInventory({
                            search: row.circuit_id || "",
                          })
                        }
                        className="border-b border-[var(--line,#e4e2dc)] last:border-0 cursor-pointer hover:bg-[var(--sunken2,#fbfaf8)]"
                      >
                        <td className="px-2 py-2.5 text-[13px] font-medium text-[var(--ink,#1c1c1a)] whitespace-nowrap">
                          {row.circuit_id || "—"}
                        </td>
                        <td className="px-2 py-2.5 text-[13px] text-[var(--ink,#1c1c1a)] max-w-[260px] truncate">
                          {row.site || "—"}
                        </td>
                        <td className="px-2 py-2.5 text-[13px] text-[var(--ink,#1c1c1a)] whitespace-nowrap">
                          {row.provider || "—"}
                        </td>
                        <td className="px-2 py-2.5 text-[13px] tabular-nums text-[var(--ink,#1c1c1a)] whitespace-nowrap">
                          {formatMoney(row.mrc)}
                        </td>
                        <td className="px-2 py-2.5 text-[13px] text-[var(--ink,#1c1c1a)] whitespace-nowrap">
                          {formatEndDate(row.end_of_term)}
                        </td>
                        <td className="px-2 py-2.5">
                          <span
                            className={`inline-flex min-w-[36px] items-center justify-center rounded-full px-2 py-0.5 text-[12px] font-semibold tabular-nums ${
                              amber
                                ? "bg-[var(--warn-soft,#fbf1e0)] text-[var(--warn,#b8791e)]"
                                : "bg-[var(--sunken,#eeece7)] text-[var(--muted,#6b6a66)]"
                            }`}
                          >
                            {days != null ? days : "—"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OverviewPage;
